import React from 'react';

interface TableSectionProps {
  content: string;
}

const TableSection: React.FC<TableSectionProps> = ({content}) => {
  const renderFormattedText = (text: string) => {
    const parts: React.ReactNode[] = [];
    let currentIndex = 0;

    const linkRegex = /\[(\*\*.*?\*\*|[^\]]+)\]\(([^)]+)\)/g;
    let match;

    while ((match = linkRegex.exec(text)) !== null) {
      if (!match[1] || !match[2] || match.index === undefined) {
        continue;
      }

      if (match.index > currentIndex) {
        const beforeText = text.substring(currentIndex, match.index);
        parts.push(...renderBoldText(beforeText));
      }

      const linkText = match[1];
      const linkUrl = match[2];
      const linkTextParts = renderBoldText(linkText);

      parts.push(
        <a
          key={`link-${match.index}`}
          href={linkUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="text-pink hover:underline">
          {linkTextParts}
        </a>
      );

      currentIndex = match.index + match[0].length;
    }

    if (currentIndex < text.length) {
      const remainingText = text.substring(currentIndex);
      parts.push(...renderBoldText(remainingText));
    }

    return parts.length > 0 ? parts : renderBoldText(text);
  };

  const renderBoldText = (text: string): React.ReactNode[] => {
    const parts = text.split(/(\*\*.*?\*\*)/g);
    return parts.map((part, idx) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        return (
          <strong key={idx} className="font-semibold">
            {part.slice(2, -2)}
          </strong>
        );
      }
      return <span key={idx}>{part}</span>;
    });
  };

  const parseMarkdownTable = (text: string) => {
    const lines = text
      .trim()
      .split('\n')
      .filter((line) => line.trim());
    if (lines.length < 2) return null;

    if (!lines[0]?.trim().startsWith('|')) return null;

    const headerLine = lines[0];
    const headers = headerLine
      .split('|')
      .map((h) => h.trim())
      .filter((h) => h.length > 0);

    const dataLines = lines.slice(2);

    const rows = dataLines.map((line) => {
      const cells = line
        .split('|')
        .map((c) => c.trim())
        .filter((c) => c.length > 0);
      return cells;
    });

    return {headers, rows};
  };

  const renderContent = () => {
    const parts = content.split('\n\n');
    const elements: React.ReactNode[] = [];

    parts.forEach((part, idx) => {
      if (!part.trim()) return;

      const table = parseMarkdownTable(part);
      if (table) {
        elements.push(
          <div key={`table-${idx}`} className="my-6">
            {/* Mobile/Tablet: Card-based layout */}
            <div className="lg:hidden space-y-4">
              {table.rows.map((row, rIdx) => (
                <div
                  key={rIdx}
                  className="border border-subtleGrey rounded-lg p-4 hover:bg-surfaceGrey transition-colors">
                  {row.map((cell, cIdx) => (
                    <div key={cIdx} className="mb-3 last:mb-0">
                      <div className="font-semibold text-headlineGrayscale mb-1 text-sm">
                        {table.headers[cIdx]?.replace(/\*\*/g, '') || ''}
                      </div>
                      <div className="text-headlineGrayscale leading-relaxed">
                        {renderFormattedText(cell)}
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>

            {/* Desktop: Table layout */}
            <div className="hidden lg:block overflow-x-auto -mx-6 lg:-mx-12">
              <div className="inline-block min-w-full px-6 lg:px-12">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="border-b-2 border-subtleGrey">
                      {table.headers.map((header, hIdx) => {
                        const cleanHeader = header.replace(/\*\*/g, '');
                        return (
                          <th
                            key={hIdx}
                            className="text-left py-3 px-4 font-semibold text-headlineGrayscale">
                            {cleanHeader}
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {table.rows.map((row, rIdx) => (
                      <tr
                        key={rIdx}
                        className="border-b border-subtleGrey hover:bg-surfaceGrey transition-colors">
                        {row.map((cell, cIdx) => {
                          return (
                            <td
                              key={cIdx}
                              className="py-4 px-4 text-headlineGrayscale leading-relaxed">
                              {renderFormattedText(cell)}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        );
      } else {
        elements.push(
          <p key={`para-${idx}`} className="mb-4">
            {renderFormattedText(part)}
          </p>
        );
      }
    });

    return elements;
  };

  return (
    <div className="prose prose-sm max-w-none text-headlineGrayscale leading-relaxed">
      {renderContent()}
    </div>
  );
};

export default TableSection;
